/**
 * One conversation in the list.
 *
 * The picture says what the conversation is before the title does: a person's
 * disc with their presence dot for a direct, a violet tile with `#` for a
 * channel, a mauve tile of people for a group, a quiet tile for the self-note.
 * Violet is the brand and a channel is a place; mauve is the accent and a group
 * is a set of people. Only the avatar is a circle — the readme is explicit that
 * nothing else in Mauvely is.
 *
 * **Presence belongs to a direct conversation only.** A dot on a group would
 * have to mean something about four people at once, and there is no such fact.
 *
 * The row is a link with its tools *beside* it rather than inside it: a button
 * nested in an anchor is two interactive elements in one, which no assistive
 * technology reads the way it looks.
 */

import { useState } from 'react';
import { ds } from '../../ds';
import { Link } from '../../router';
import { useT } from '../../i18n';
import { formatTime } from '../../i18n/format';
import { store, useStore } from '../../lib/relay/store';
import { Avatar } from '../Avatar';
import { VerifiedMark } from './Official';
import type { Conversation } from '../../lib/relay/types';
import './chat.css';

/** The tile for everything that is not a person. */
function Tile({ kind }: { kind: Conversation['kind'] }) {
  const { Icon } = ds();
  const tone =
    kind === 'channel' ? 'rl-tile-channel' : kind === 'group' ? 'rl-tile-group' : 'rl-tile-quiet';
  const icon = kind === 'channel' ? 'Channel' : kind === 'group' ? 'Group' : 'SelfNote';

  return (
    <span aria-hidden="true" className={`rl-tile ${tone}`}>
      <Icon name={icon} size={18} />
    </span>
  );
}

/** Pin, mute and archive. A small menu rather than three buttons, because
 *  three permanent buttons on every row is a list of buttons with a chat
 *  behind it. */
function RowMenu({ conversation }: { conversation: Conversation }) {
  const { Icon, IconButton } = ds();
  const t = useT();
  const [open, setOpen] = useState(false);

  const item = (label: string, run: () => void) => (
    <button
      type="button"
      onClick={() => {
        run();
        setOpen(false);
      }}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-6) var(--space-12)',
        border: 0,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );

  return (
    <span className="rl-tools" style={{ position: 'relative' }}>
      {conversation.muted && <Icon name="SpeakerOff" size={14} />}
      <IconButton
        icon="Overflow"
        label={t('common.more')}
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <span
          role="menu"
          onMouseLeave={() => setOpen(false)}
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            zIndex: 40,
            minWidth: 160,
            padding: 'var(--space-6) 0',
            borderRadius: 'var(--radius-md, 10px)',
            background: 'var(--surface-raised)',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          {item(conversation.pinned ? t('chats.unpin') : t('chats.pin'), () =>
            store.setPinned?.(conversation.id, !conversation.pinned)
          )}
          {item(conversation.muted ? t('chats.unmute') : t('chats.mute'), () =>
            store.setMuted?.(conversation.id, !conversation.muted)
          )}
          {item(conversation.archived ? t('chats.unarchive') : t('chats.archive'), () =>
            store.setArchived?.(conversation.id, !conversation.archived)
          )}
        </span>
      )}
    </span>
  );
}

export function ConversationRow({
  conversation,
  active
}: {
  conversation: Conversation;
  active: boolean;
}) {
  const { Badge, Icon } = ds();
  const t = useT();
  const me = useStore((s) => s.me());

  // A direct conversation's picture is the other person's, and so is its dot.
  const other = conversation.kind === 'direct'
    ? conversation.members.find((id) => id !== me?.id)
    : undefined;
  const people = useStore((s) => s.people(other ? [other] : []));
  const person = people[0];

  return (
    <div className="rl-convrow" data-active={active}>
      <Link
        to={`/c/${conversation.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-12)',
          flex: 1,
          minWidth: 0
        }}
      >
        {person ? (
          <Avatar person={person} presence={person.presence} />
        ) : (
          <Tile kind={conversation.kind} />
        )}

        {/* Two lines: who and when, then what was said and how much of it is
            unread. The app's row is 60px and this is what fills it. */}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="rl-row-line">
            <span className="rl-truncate rl-row-title">
              {conversation.title}
              {conversation.official && <VerifiedMark size={13} />}
            </span>
            <span className="rl-row-time">{formatTime(conversation.lastAt)}</span>
          </span>
          <span className="rl-row-line">
            <span className="rl-truncate rl-row-preview">{conversation.preview}</span>
            {conversation.mentioned && (
              <span role="img" aria-label={t('chats.mentioned')} title={t('chats.mentioned')}>
                <Icon name="Mention" size={14} color="var(--primary-text)" />
              </span>
            )}
            {conversation.unread > 0 && (
              <Badge tone="primary">{conversation.unread}</Badge>
            )}
          </span>
        </span>
      </Link>

      <RowMenu conversation={conversation} />
    </div>
  );
}
